import React, { FC } from 'react';
import translate from '@translate';
import Grid, { GridItem } from 'src/components/Grid';
import TextField from 'src/components/TextField';
import Typography from 'src/components/Typography';
import { WebhookDeliveryRowType } from 'src/types/webhookDeliveriesGrid';
import { formatWebhookDateTime } from '../WebhookDetailContainer/functions';
import { getStatusColor, isAutoRetryInProgress, getRetryBlockedReason } from '../WebhookRecordsListContainer/functions';
import PayloadSection from './PayloadSection';
import { TableRouterLink } from 'src/components/TableLink';

type DetailProps = {
    data: WebhookDeliveryRowType;
};

const Detail: FC<DetailProps> = (props) => {
    const { data } = props;
    const nextRetryLabel =
        isAutoRetryInProgress(data) && data.NEXT_RETRY_AT
            ? formatWebhookDateTime(data.NEXT_RETRY_AT)
            : '';
    const retryBlockedReason = getRetryBlockedReason(data);

    return (
        <Grid>
            <GridItem cols={6}>
                <TextField
                    value={data.EVENT_ID}
                    onChange={() => undefined}
                    name="EVENT_ID"
                    label={translate('webhookRecords.table.eventId')}
                    fullWidth
                    disabled
                />
            </GridItem>
            <GridItem cols={3}>
                <TextField
                    value={translate(`webhooks.webhookType.${data.WEBHOOK_TYPE}`)}
                    onChange={() => undefined}
                    name="WEBHOOK_TYPE"
                    label={translate('webhookRecords.table.webhookType')}
                    fullWidth
                    disabled
                />
            </GridItem>
            <GridItem cols={3}>
                <TextField
                    value={data.METHOD}
                    onChange={() => undefined}
                    name="METHOD"
                    label={translate('webhookRecords.table.method')}
                    fullWidth
                    disabled
                />
            </GridItem>
            <GridItem cols={9}>
                <TextField
                    value={data.URL}
                    onChange={() => undefined}
                    name="URL"
                    label={translate('webhookRecords.table.url')}
                    fullWidth
                    disabled
                />
            </GridItem>
            <GridItem cols={3}>
                <TextField
                    value={data.WEBHOOK_ID}
                    onChange={() => undefined}
                    name="WEBHOOK_ID"
                    label={translate('webhookRecords.table.webhookId')}
                    fullWidth
                    disabled
                />
            </GridItem>
            <GridItem cols={3}>
                <TextField
                    value={formatWebhookDateTime(data.SENDING_TIME)}
                    onChange={() => undefined}
                    name="SENDING_TIME"
                    label={translate('webhookRecords.table.sendingTime')}
                    fullWidth
                    disabled
                />
            </GridItem>
            <GridItem cols={3}>
                <TextField
                    value={formatWebhookDateTime(data.LAST_ATTEMPT_AT)}
                    onChange={() => undefined}
                    name="LAST_ATTEMPT_AT"
                    label={translate('webhookRecordDetail.lastAttemptAt')}
                    fullWidth
                    disabled
                />
            </GridItem>
            <GridItem cols={3}>
                <Typography variant="caption" display="block">
                    {translate('webhookRecords.table.statusCode')}
                </Typography>
                <Typography variant="body1" sx={{ color: getStatusColor(data.STATUS_CODE), mt: 1 }}>
                    {data.STATUS_CODE}
                </Typography>
            </GridItem>
            <GridItem cols={3}>
                <TextField
                    value={data.ATTEMPT_COUNT ?? data.ATTEMPTS?.length ?? 0}
                    onChange={() => undefined}
                    name="ATTEMPT_COUNT"
                    label={translate('webhookRecords.table.attemptCount')}
                    fullWidth
                    disabled
                />
            </GridItem>
            {nextRetryLabel && (
                <GridItem cols={6}>
                    <TextField
                        value={nextRetryLabel}
                        onChange={() => undefined}
                        name="NEXT_RETRY_AT"
                        label={translate('webhookRecordDetail.nextRetryAt')}
                        fullWidth
                        disabled
                    />
                </GridItem>
            )}
            {data.RETRY_OF_EVENT_ID && (
                <GridItem cols={6}>
                    <Typography variant="caption" display="block">
                        {translate('webhookRecordDetail.retryOfEventId')}
                    </Typography>
                    <TableRouterLink to={`/settings/webhook_records/${data.RETRY_OF_EVENT_ID}`}>
                        {data.RETRY_OF_EVENT_ID}
                    </TableRouterLink>
                </GridItem>
            )}
            {retryBlockedReason && (
                <GridItem cols={12}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {translate('webhookRecordDetail.retryUnavailable')}:{' '}
                        {translate(`webhookRecords.retryBlocked.${retryBlockedReason}`)}
                    </Typography>
                </GridItem>
            )}
            <GridItem cols={12}>
                <PayloadSection method={data.METHOD} payload={data.PAYLOAD} />
            </GridItem>
        </Grid>
    );
};

export default Detail;
